/**
 * Event schemas — the append-only log's vocabulary.
 *
 * The event log is the source of truth (docs/design/02-data-model.md §1); current
 * state is a projection over it. These schemas are the contract for what may enter
 * that log, and they are validated on both sides: the client before enqueueing,
 * the Lambda before writing. Both import this file, so the two cannot drift
 * (ADR-0003).
 *
 * Payloads are strict objects. An unknown key is a rejection rather than a
 * silently dropped field, because sync protocol §3 promises that "one bad event
 * rejects that event only, not the batch" -- which is only a useful promise if
 * "bad" is detected rather than tolerated. A stale client sends *fewer* fields,
 * never more, so strictness does not break an out-of-date PWA bundle.
 *
 * Vocabulary is binding: see docs/requirements/04-glossary-and-domain-model.md.
 * Intentions are Keep, Uncertain, Release, Invest; the absence of any intention
 * record is Unreviewed and is deliberately not a value here.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
/**
 * An encoded hybrid logical clock, `<12 hex>-<4 hex>-<deviceId>`.
 *
 * Validated by shape rather than by calling `decode`, so that a schema failure
 * and a parse failure cannot disagree. The widths must match hlc.ts exactly:
 * they are what make lexicographic order equal causal order.
 */
export const hlcString = z.string().regex(/^[0-9a-f]{12}-[0-9a-f]{4}-\S+$/, 'malformed hlc');
/** Client-generated UUIDv7 (ids.ts). Time-ordered, and the idempotency key. */
export const entityId = z.uuid();
/** A device's stable identifier. Appears inside the hlc, so it may not contain whitespace. */
export const deviceId = z
    .string()
    .min(1)
    .max(64)
    .regex(/^\S+$/, 'deviceId may not contain whitespace');
/** An instant in UTC. Audit timestamps only -- never a user-meant date (NFR-FUNC-4). */
export const instant = z.iso.datetime();
/**
 * A calendar date with no time and no zone, because that is what the user means
 * by an acquisition, use, or review-again date (NFR-FUNC-4).
 */
export const calendarDate = z.iso.date();
/**
 * Money: an integer in minor units plus an explicit currency code. Never a float,
 * anywhere (NFR-FUNC-2, NFR-FUNC-3).
 */
export const money = z.strictObject({
    minor: z.int().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/, 'ISO 4217 currency code'),
});
/**
 * Text length caps. Not specified by any requirement -- chosen to bound a single
 * event's size, since an unbounded `notes` field is the easy way to exceed
 * DynamoDB's 400 KB item limit and take the whole batch's transaction with it.
 */
const shortText = z.string().max(200);
const longText = z.string().max(10_000);
/**
 * The pre-2026-10-09 name for an investment entry's `referencePrice`.
 *
 * Accepted so events already in the log stay parseable -- see the note above the
 * investment events for why that is permanent -- and never produced. Nothing in
 * this codebase writes it, which is asserted by a test rather than left to
 * discipline.
 */
const deprecatedTargetPrice = money.nullable().optional();
// ---------------------------------------------------------------------------
// Enumerations — all drawn from the glossary or a numbered requirement
// ---------------------------------------------------------------------------
/** Glossary: exactly one per item at a time. Unreviewed is the absence of a record. */
export const intention = z.enum(['keep', 'uncertain', 'release', 'invest']);
/** FR-7.2. */
export const releaseKind = z.enum([
    'donated',
    'sold',
    'gifted',
    'recycled',
    'discarded',
    'returned',
]);
/** FR-4.4. */
export const customFieldType = z.enum([
    'text',
    'number',
    'date',
    'single-select',
    'multi-select',
    'boolean',
]);
/**
 * Condition is free text, and that is settled rather than pending.
 *
 * No requirement enumerates its values, and inventing six here would bind the UI
 * to a vocabulary nobody agreed to -- the glossary exists to own decisions like
 * that. The question was whether FR-3.3's filter needs a closed set, and it does
 * not: that same requirement filters by `brand` and `color`, which are free text
 * too. All three filter as a facet over the values actually present in the
 * manifest, which is also the only kind of filter that stays useful when someone
 * writes "lightly worn" rather than picking from a list.
 */
export const condition = shortText;
// ---------------------------------------------------------------------------
// Item fields — the merge surface
// ---------------------------------------------------------------------------
/**
 * `item.fieldSet` carries one field and its new value, and is the only event
 * subject to per-field last-writer-wins (docs/design/02-data-model.md §3).
 *
 * Discriminated on `field` so each field's value type is checked, rather than
 * accepting `unknown` and discovering at merge time that a price arrived as a
 * float. This is the highest-value validation in the file: these writes are the
 * ones that can conflict, and a malformed one corrupts the projection.
 *
 * Descriptors (brand, model, color, size, material) are first-class fields here
 * rather than custom field values, per FR-2.15 and the glossary.
 */
const nullableShortText = shortText.nullable();
export const itemFieldSet = z.discriminatedUnion('field', [
    // The only required field on an item (glossary, ITEM), so it is not nullable.
    z.strictObject({ field: z.literal('name'), value: shortText.min(1) }),
    z.strictObject({ field: z.literal('brand'), value: nullableShortText }),
    z.strictObject({ field: z.literal('model'), value: nullableShortText }),
    z.strictObject({ field: z.literal('color'), value: nullableShortText }),
    z.strictObject({ field: z.literal('size'), value: nullableShortText }),
    z.strictObject({ field: z.literal('material'), value: nullableShortText }),
    z.strictObject({ field: z.literal('acquiredFrom'), value: nullableShortText }),
    z.strictObject({ field: z.literal('categoryId'), value: entityId.nullable() }),
    z.strictObject({ field: z.literal('locationId'), value: entityId.nullable() }),
    z.strictObject({ field: z.literal('condition'), value: condition.nullable() }),
    // An item may represent several identical units (glossary, ITEM).
    z.strictObject({ field: z.literal('quantity'), value: z.int().min(1) }),
    z.strictObject({ field: z.literal('purchasePrice'), value: money.nullable() }),
    z.strictObject({ field: z.literal('estimatedValue'), value: money.nullable() }),
    z.strictObject({ field: z.literal('acquiredOn'), value: calendarDate.nullable() }),
    /**
     * A last-used date from before the catalog existed (FR-6.6).
     *
     * Stored as a field rather than as a `USE#` row because the glossary is
     * explicit that it must not fabricate one: a use record that never happened
     * would inflate `useCount` and make cost per use (FR-6.4) wrong. The derived
     * `lastUsedOn` is the later of this and the newest real use.
     */
    // Retained so events already in the log still parse; nothing reads it since
    // use tracking was removed. See the note on `use.recorded` in `merge.ts`.
    z.strictObject({ field: z.literal('priorLastUsedOn'), value: calendarDate.nullable() }),
    z.strictObject({ field: z.literal('notes'), value: longText.nullable() }),
    // NFR-SAFE-3: excludes a sentimental item from bulk operations.
    z.strictObject({ field: z.literal('protected'), value: z.boolean() }),
    /**
     * Picked out by the owner, for no reason the system is entitled to ask about.
     *
     * A flag rather than an intention: `keep` is a decision reached by reviewing,
     * and this is just the shelf someone wants to find again. Conflating them
     * would make the review's four answers mean something they do not.
     */
    z.strictObject({ field: z.literal('favorite'), value: z.boolean() }),
    /**
     * `colour`, which `color` replaced on 2026-10-09.
     *
     * Accepted so events already in the log stay parseable, and that is permanent
     * rather than a courtesy: the log is append-only and FR-9.10 replays it to
     * reconstruct any past date, so events carrying the old name exist for as long
     * as the account does. A schema that rejected them would make them
     * unparseable, and `Replica.log` skips what it cannot parse -- so every
     * recoloured item would quietly lose that field from any historical answer.
     *
     * Nothing writes it. The merge maps it onto `color` as it projects (see
     * `FIELD_ALIASES` in `merge.ts`), so there is one cell, one clock and one name
     * everywhere downstream -- no consumer has to know this ever had two.
     */
    z.strictObject({ field: z.literal('colour'), value: nullableShortText }),
]);
/**
 * Field names that are accepted on the way in and never produced.
 *
 * Excluded from `itemFieldNames`, so a capture form built from that list does
 * not render a second, retired field beside its replacement.
 */
export const RETIRED_ITEM_FIELDS = ['colour'];
/**
 * The field names a projected item carries.
 *
 * Retired names are left out. They are still accepted on an event, but the merge
 * maps them onto their replacement as it projects, so no cell is ever keyed by
 * one -- and anything built from this list (the capture form, the CSV columns,
 * the searchable fields) would otherwise grow a duplicate of a field it already
 * has.
 */
export const itemFieldNames = itemFieldSet.options
    .map((o) => o.shape.field.value)
    .filter((name) => !RETIRED_ITEM_FIELDS.includes(name));
/**
 * `item.created` carries initial values for any subset of the mergeable fields.
 *
 * Spelled out rather than derived from `itemFieldSet` by mapping over its options.
 * That derivation worked at runtime but inferred as `{ name: string }`, which
 * would have let `quantity: 'six'` typecheck -- worse than the duplication, on
 * the one surface where a wrong value corrupts the projection. The two are kept
 * in step by a test asserting their field sets match exactly.
 */
export const itemCreated = z.strictObject({
    name: shortText.min(1),
    brand: nullableShortText.optional(),
    model: nullableShortText.optional(),
    color: nullableShortText.optional(),
    /** Retired; see `RETIRED_ITEM_FIELDS`. Accepted, never written. */
    colour: nullableShortText.optional(),
    size: nullableShortText.optional(),
    material: nullableShortText.optional(),
    acquiredFrom: nullableShortText.optional(),
    categoryId: entityId.nullable().optional(),
    locationId: entityId.nullable().optional(),
    condition: condition.nullable().optional(),
    quantity: z.int().min(1).optional(),
    purchasePrice: money.nullable().optional(),
    estimatedValue: money.nullable().optional(),
    acquiredOn: calendarDate.nullable().optional(),
    priorLastUsedOn: calendarDate.nullable().optional(),
    notes: longText.nullable().optional(),
    protected: z.boolean().optional(),
    favorite: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Tag names
// ---------------------------------------------------------------------------
/**
 * Normalizes a tag name for uniqueness comparison (FR-4.14): case- and
 * whitespace-insensitive, while the display name preserves what the user typed.
 *
 * Lives here rather than in the client because both sides compare tag names, and
 * two implementations that disagreed would let a duplicate tag exist on one
 * device and not the other. Uses `toLowerCase` rather than `toLocaleLowerCase`
 * deliberately: the result must be identical on every device regardless of locale.
 */
export function normalizeTagName(displayName) {
    return displayName.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
}
// ---------------------------------------------------------------------------
// The event union
// ---------------------------------------------------------------------------
/**
 * Fields every event carries (docs/design/02-data-model.md §4).
 *
 * `entityId` is the id of the entity the event projects onto -- for `tag.applied`
 * that is the item, not the tag, since the item is what changes shape.
 */
const envelope = {
    /** Client-generated; the idempotency key written with `attribute_not_exists`. */
    eventId: z.uuid(),
    hlc: hlcString,
    deviceId,
    entityId,
    /** As the device saw it. Advisory: ordering comes from `hlc`, never from this. */
    clientTs: instant,
    /** Set on acceptance. Advisory only, per §4. */
    serverTs: instant.optional(),
};
const event = (type, payload) => z.strictObject({ ...envelope, type: z.literal(type), payload });
const empty = z.strictObject({});
export const anyEvent = z.discriminatedUnion('type', [
    // --- items -------------------------------------------------------------
    event('item.created', itemCreated),
    event('item.fieldSet', itemFieldSet),
    // Soft, with a 30-day recovery window (FR-3.8). Sets `deletedHlc`.
    event('item.deleted', empty),
    event('item.restored', empty),
    /**
     * A custom field's value on an item. Kept as its own event rather than folded
     * into `item.fieldSet` because values are separate rows, which is what keeps
     * them searchable (glossary, CUSTOM_FIELD_DEF / CUSTOM_FIELD_VALUE).
     *
     * The value is only shape-checked here: whether it matches its definition's
     * type depends on a `field.defined` event this schema cannot see, so the merge
     * step enforces that.
     */
    event('item.customFieldSet', z.strictObject({
        fieldId: entityId,
        value: z.union([shortText, z.number(), z.boolean(), z.array(shortText).max(50)]).nullable(),
    })),
    // --- intention ---------------------------------------------------------
    // Append-only; history is never rewritten (FR-5.4).
    event('intention.set', z.strictObject({
        intention,
        reason: longText.optional(),
        reviewAgainOn: calendarDate.optional(),
    })),
    // --- use ---------------------------------------------------------------
    // Append-only, one row per use, and the date may be backdated (glossary).
    event('use.recorded', z.strictObject({ useId: entityId, onDate: calendarDate })),
    event('use.deleted', z.strictObject({ useId: entityId })),
    // --- tags --------------------------------------------------------------
    event('tag.created', z.strictObject({
        displayName: shortText.min(1),
        normalizedName: shortText.min(1),
        /**
         * FR-4.20, which nothing writes yet.
         *
         * Renamed outright on 2026-10-09, with no back-compatible alias -- unlike
         * an item's `color`, which keeps accepting `colour` forever because events
         * carrying it are in the log. No event has ever set this one, so there is
         * nothing to stay compatible with.
         */
        color: shortText.nullable().optional(),
    })),
    /**
     * Changes the display name only. The tag's identity never changes, which is what
     * keeps "how many tee-shirts did I own last March" correct after a rename
     * (FR-9.10, and the §4 note on tag renames).
     */
    event('tag.renamed', z.strictObject({ displayName: shortText.min(1), normalizedName: shortText.min(1) })),
    event('tag.deleted', empty),
    event('tag.merged', z.strictObject({ intoTagId: entityId })),
    // Recorded as events, not flags, so tag membership is reconstructable (FR-9.11).
    event('tag.applied', z.strictObject({ itemId: entityId, tagId: entityId })),
    event('tag.removed', z.strictObject({ itemId: entityId, tagId: entityId })),
    // --- categories and locations -----------------------------------------
    // Nesting depth (FR-4.3, max 3) is a tree property this schema cannot see; the
    // merge step enforces it.
    event('category.created', z.strictObject({ name: shortText.min(1), parentId: entityId.nullable().optional() })),
    event('category.updated', z.strictObject({
        name: shortText.min(1).optional(),
        parentId: entityId.nullable().optional(),
    })),
    event('category.deleted', empty),
    event('location.created', z.strictObject({ name: shortText.min(1), parentId: entityId.nullable().optional() })),
    event('location.updated', z.strictObject({
        name: shortText.min(1).optional(),
        parentId: entityId.nullable().optional(),
    })),
    event('location.deleted', empty),
    // --- custom field definitions -----------------------------------------
    // Belongs to a category and is inherited by descendants (FR-4.5).
    event('field.defined', z.strictObject({
        categoryId: entityId,
        name: shortText.min(1),
        type: customFieldType,
        options: z.array(shortText).max(100).optional(),
    })),
    /**
     * `categoryId` is required on update and delete as well as on definition.
     *
     * The row's sort key is `FIELD#<categoryId>#<fieldId>` (data model §2.1), so
     * without it the event cannot address its own row: the server would have to
     * scan the `FIELD#` prefix to find it, and an update arriving before its
     * definition could not be applied at all. Same rule as the photo events -- an
     * event carries enough to locate what it changes.
     */
    event('field.updated', z.strictObject({
        categoryId: entityId,
        name: shortText.min(1).optional(),
        options: z.array(shortText).max(100).optional(),
    })),
    event('field.deleted', z.strictObject({ categoryId: entityId })),
    // --- photos ------------------------------------------------------------
    /**
     * Emitted at capture, before the bytes have uploaded: the item is usable
     * immediately and the uploader catches up (sync protocol §4).
     */
    /**
     * For every photo event the envelope's `entityId` is the *item*, and the photo
     * is named in the payload. Carrying the item on all of them is what lets a
     * `photo.uploaded` that arrives before its `photo.added` still be applied
     * instead of silently dropped, which is required for order-independence.
     */
    event('photo.added', z.strictObject({
        photoId: entityId,
        originalKey: shortText,
        derivativeKey: shortText,
        width: z.int().min(1),
        height: z.int().min(1),
        byteSize: z.int().min(1),
    })),
    /** The upload completed. From sync protocol §4 rather than the §4 event table. */
    event('photo.uploaded', z.strictObject({ photoId: entityId })),
    event('photo.removed', z.strictObject({ photoId: entityId })),
    /** Full ordering, not a pairwise swap, so the result cannot depend on arrival order. */
    event('photo.reordered', z.strictObject({ photoIds: z.array(entityId).min(1).max(100) })),
    event('photo.primarySet', z.strictObject({ photoId: entityId })),
    // --- release -----------------------------------------------------------
    /**
     * At most one per item, and its existence is what makes the item archived --
     * the two are not tracked separately, so they cannot disagree (FR-7.4).
     */
    event('release.recorded', z.strictObject({
        outcome: releaseKind,
        onDate: calendarDate,
        recipient: shortText.optional(),
        proceeds: money.optional(),
        note: longText.optional(),
    })),
    // FR-7.5: restores an archived item to active.
    event('release.cleared', empty),
    // --- investment entries ------------------------------------------------
    /**
     * `referencePrice` replaced `targetPrice` on 2026-10-09 (FR-8.1).
     *
     * The wording changed because "target" reads as a figure being worked towards,
     * which turns a list of things under consideration into a list of intended
     * purchases -- the nudge `NFR-SAFE-2` forbids.
     *
     * `targetPrice` is still accepted, and that is permanent rather than a
     * courtesy. The log is append-only and FR-9.10 replays it to reconstruct any
     * past date, so events carrying the old key exist for as long as the account
     * does. A schema that rejected them would make them unparseable, and
     * `Replica.log` skips what it cannot parse -- so every investment entry would
     * quietly vanish from any historical answer. It is accepted on the way in and
     * never produced on the way out, which `mutations.test.ts` asserts rather than
     * leaving to discipline. The two are resolved by clock, like any other pair of
     * writes to one field, so a device still running the previous build cannot
     * have its later edit discarded (FR-10.6).
     */
    event('investment.created', z.strictObject({
        name: shortText.min(1),
        categoryId: entityId.nullable().optional(),
        notes: longText.optional(),
        referencePrice: money.nullable().optional(),
        targetPrice: deprecatedTargetPrice,
        priority: z.int().min(0).max(100).optional(),
    })),
    event('investment.updated', z.strictObject({
        name: shortText.min(1).optional(),
        categoryId: entityId.nullable().optional(),
        notes: longText.optional(),
        referencePrice: money.nullable().optional(),
        targetPrice: deprecatedTargetPrice,
        priority: z.int().min(0).max(100).optional(),
    })),
    // FR-8.2: many-to-many against the items it would replace.
    event('investment.linked', z.strictObject({ itemId: entityId })),
    event('investment.unlinked', z.strictObject({ itemId: entityId })),
    // FR-8.4: acquiring creates a catalog item, linked here.
    /**
     * Acquired and abandoned carry distinctly named dates, not a shared `onDate`.
     *
     * Both are merged into the same field map on the entry, so one key would mean
     * two different things and the later write would silently redefine the earlier
     * one -- an entry abandoned on one device and acquired on another would end up
     * with a single date belonging to neither event.
     */
    event('investment.acquired', z.strictObject({ acquiredItemId: entityId, acquiredOn: calendarDate })),
    // FR-8.5: abandoned, with a reason.
    event('investment.abandoned', z.strictObject({ abandonedOn: calendarDate, reason: longText.optional() })),
    event('investment.deleted', empty),
]);
/** Every event type, for exhaustiveness checks in the projection. */
export const eventTypes = anyEvent.options.map((o) => o.shape.type.value);
/**
 * Splits a batch into what may be written and what may not.
 *
 * Implements sync protocol §3 step 1: "one bad event rejects that event only, not
 * the batch." Returning both halves rather than throwing is the point -- a push
 * containing one malformed event must still durably store the other 199, or the
 * client has no way to make progress past it.
 */
export function partitionEvents(input) {
    const accepted = [];
    const rejected = [];
    input.forEach((candidate, index) => {
        const result = anyEvent.safeParse(candidate);
        if (result.success) {
            accepted.push(result.data);
            return;
        }
        const eventId = typeof candidate === 'object' &&
            candidate !== null &&
            'eventId' in candidate &&
            typeof candidate.eventId === 'string'
            ? candidate.eventId
            : undefined;
        rejected.push({
            index,
            ...(eventId === undefined ? {} : { eventId }),
            message: result.error.issues
                .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
                .join('; '),
        });
    });
    return { accepted, rejected };
}
//# sourceMappingURL=events.js.map